import { queryOptions } from "@tanstack/react-query"

import { http } from "@/lib/http"
import { MAX_PAGE_SIZE, type PageWire } from "@/lib/pagination"
import { qk } from "@/lib/query-keys"
import { ASSIGNABLE_ROLES } from "@/lib/roles"

/*
   The staff an admin can narrow the queue to: everyone who can hold a review.

   `GET /api/v1/users` is admin-only, so the caller decides whether this runs
   (`enabled`) — anyone else would get a 403, and the queue must never ask.
*/

/** The fields of `UserOut` the Assignee options need. */
export interface AssignableStaffWire {
  id: number
  email: string
  full_name: string | null
  role: string
  is_active: boolean
}

async function fetchAssignableStaff(signal: AbortSignal): Promise<AssignableStaffWire[]> {
  const fetchPage = (page: number) =>
    http.get<PageWire<AssignableStaffWire>>("/api/v1/users", {
      params: { role: [...ASSIGNABLE_ROLES], page, page_size: MAX_PAGE_SIZE },
      signal,
    })

  // The backend caps a page at MAX_PAGE_SIZE, so a roster beyond it arrives in pages.
  const first = await fetchPage(1)
  const pageCount = Math.ceil(first.total / MAX_PAGE_SIZE)
  // Pages after the first. An empty roster (total 0) gives pageCount 0, so clamp to 0.
  const rest = await Promise.all(
    Array.from({ length: Math.max(pageCount - 1, 0) }, (_, i) => fetchPage(i + 2)),
  )
  return [first, ...rest].flatMap((p) => p.items)
}

export const assignableStaffQuery = ({ enabled }: { enabled: boolean }) =>
  queryOptions({
    // Its own key rather than a shared roster's: the queue keeps the wire as it arrived.
    queryKey: qk.users.list({ role: ASSIGNABLE_ROLES, view: "queue-assignee" }),
    queryFn: ({ signal }) => fetchAssignableStaff(signal),
    enabled,
    staleTime: 60_000,
  })
